<template>
  <div class="cat-socionegocio">
    <!-- Loading state del control de acceso -->
    <div v-if="accessLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Verificando acceso...</span>
      </div>
    </div>
    
    <!-- Contenido principal cuando se tiene acceso -->
    <div v-else-if="canAccess" class="cat-socionegocio-content">
      <!-- Toolbar Component -->
      <div class="toolbar-container">
        <ToolbarComponent
          :buttonGroups="[
            {
              id: 'main-actions',
              buttons: [
                {
                  id: 'nuevo',
                  text: 'Nuevo',
                  icon: 'bi bi-plus',
                  variant: 'primary',
                  event: 'new'
                },
                {
                  id: 'actualizar',
                  text: 'Actualizar',
                  icon: 'bi bi-arrow-clockwise',
                  variant: 'secondary',
                  event: 'refresh'
                },
                {
                  id: 'descargar',
                  text: 'Descargar',
                  icon: 'bi bi-download',
                  variant: 'success',
                  event: 'download'
                }
              ]
            }
          ]"
          :rightInfo="`Total: ${sociosNegocio.length} socios de negocio`"
          @new="addSocioNegocio"
          @refresh="loadSociosNegocio"
          @download="downloadBulk"
        />
      </div>
      
      <!-- Contenido de la página -->
      <div class="page-content">
        <div class="alert alert-info" v-if="loading">Cargando socios de negocio...</div>
        <div class="alert alert-danger" v-if="error">{{ error }}</div>
        
        <div v-if="!loading && !error && (!sociosNegocio || sociosNegocio.length === 0)">
          <div v-if="!hasEmpresa" class="alert alert-warning">
            Debe elegir una empresa antes de poder continuar.
          </div>
          <div v-else class="alert alert-info">
            No hay socios de negocio registrados. Haga clic en 'Nuevo' para agregar uno.
          </div>
        </div>

        <!-- Tabla con MyDataGrid -->
        <div v-if="!loading && !error && sociosNegocio && sociosNegocio.length > 0">
          <MyDataGrid
            :columns="dataGridColumns"
            :rows="sociosNegocio"
            :enable-pagination="true"
            :per-page="10"
            :page-sizes="[5, 10, 25, 50]"
            :initial-sort="{ key: 'Clave', dir: 'asc' }"
            filter-placeholder="Buscar socios de negocio..."
            :striped="true"
            :compact="true"
            :show-actions="true"
          >
            <!-- Slot personalizado para la columna EsActivo -->
            <template #cell-EsActivo="{ value }">
              <span v-if="value" class="badge bg-success">Sí</span>
              <span v-else class="badge bg-secondary">No</span>
            </template>
            
            <!-- Slot de acciones personalizadas -->
            <template #actions="{ row }">
              <button 
                @click="editSocioNegocio(row)" 
                class="btn btn-sm btn-outline-primary me-1"
                title="Editar socio de negocio"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="confirmDelete(row.Clave)" 
                class="btn btn-sm btn-outline-danger"
                title="Eliminar socio de negocio"
              >
                <i class="fas fa-trash"></i>
              </button>
            </template>
          </MyDataGrid>
        </div>

        <div class="mt-4">
          <router-link to="/" class="btn btn-secondary">
            <i class="bi bi-arrow-left me-1"></i> Volver al Menú
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- Mensaje cuando no se tiene acceso -->
    <div v-else class="alert alert-warning text-center">
      <h4>Acceso Restringido</h4>
      <p>No tiene permisos para acceder a esta página.</p>
      <router-link to="/" class="btn btn-primary">Volver al Inicio</router-link>
    </div>

    <!-- Modal para Nuevo/Editar Socio de Negocio -->
    <div 
      class="modal fade" 
      :class="{ 'show d-block': showModal }"
      id="socionegocioModal" 
      tabindex="-1" 
      aria-labelledby="socionegocioModalLabel" 
      :aria-hidden="!showModal"
      @click.self="closeModal"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="socionegocioModalLabel">
              <i class="bi bi-plus-circle me-2" v-if="!isEditing"></i>
              <i class="bi bi-pencil me-2" v-else></i>
              {{ isEditing ? 'Editar Socio de Negocio' : 'Nuevo Socio de Negocio' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <CatSocioNegocioForm
              :socioNegocio="formData"
              :isEditing="isEditing"
              :loading="modalLoading"
              @submit="handleSubmit"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { useAccessControl } from '@/composables/useAccessControl'
import CatSocioNegocioService from '../controllers/CatSocioNegocioController';
import MyDataGrid from '@/shared/components/MyDataGrid.vue';
import ToolbarComponent from '@/shared/components/ToolbarComponent.vue';
import CatSocioNegocioForm from '../components/CatSocioNegocioForm.vue';

export default {
  name: 'CatSocioNegocioView',
  components: {
    MyDataGrid,
    ToolbarComponent,
    CatSocioNegocioForm
  },
  setup() {
    const { 
      requiresAuth, 
      requiresMenuPermission, 
      canAccess, 
      isLoading: accessLoading 
    } = useAccessControl({
      requiresAuth: true,
      requiresMenuPermission: false
    })

    return {
      requiresAuth,
      requiresMenuPermission,
      canAccess,
      accessLoading
    }
  },
  data() {
    return {
      service: new CatSocioNegocioService(),
      // Modal state
      showModal: false,
      modalLoading: false,
      isEditing: false,
      formData: {
        Clave: '',
        EmpresaId: 1,
        ClaveErp: '',
        TipoSocio: '',
        NombreComercial: '',
        RazonSocial: '',
        RFC: '',
        CURP: '',
        Calle: '',
        NumExt: '',
        NumInt: '',
        Colonia: '',
        Localidad: '',
        Municipio: '',
        Estado: '',
        Pais: '',
        CP: '',
        Telefono1: '',
        Telefono2: '',
        Email: '',
        ContactoNombre1: '',
        ContactoPuesto1: '',
        ContactoTelefono1: '',
        ContactoEmail1: '',
        MonedaDefault: '',
        MetodoPagoDefault: '',
        FormaPagoDefault: '',
        UsoCFDIDefault: '',
        RegimenFiscal: '',
        DiasCredito: 0,
        LimiteCredito: 0,
        SaldoActual: 0,
        EsActivo: true,
        Notas: ''
      }
    };
  },
  computed: {
    sociosNegocio() {
      return this.service.sociosNegocio;
    },
    loading() {
      return this.service.loading;
    },
    error() {
      return this.service.error;
    },
    hasEmpresa() {
      const emp = JSON.parse(localStorage.getItem('empresa_seleccionada') || 'null');
      return !!(emp && emp.EmpresaId);
    },
    // Configuración de columnas para MyDataGrid
    dataGridColumns() {
      return [
        { key: 'Clave', label: 'Clave', sortable: true },
        { key: 'EmpresaId', label: 'ID Empresa', sortable: true },
        { key: 'ClaveErp', label: 'Clave ERP', sortable: true },
        { key: 'TipoSocio', label: 'Tipo Socio', sortable: true },
        { key: 'NombreComercial', label: 'Nombre Comercial', sortable: true },
        { key: 'RazonSocial', label: 'Razón Social', sortable: true },
        { key: 'RFC', label: 'RFC', sortable: true },
        { key: 'CURP', label: 'CURP', sortable: true },
        { key: 'Calle', label: 'Calle', sortable: true },
        { key: 'NumExt', label: 'Núm. Ext.', sortable: true },
        { key: 'NumInt', label: 'Núm. Int.', sortable: true },
        { key: 'Colonia', label: 'Colonia', sortable: true },
        { key: 'Localidad', label: 'Localidad', sortable: true },
        { key: 'Municipio', label: 'Municipio', sortable: true },
        { key: 'Estado', label: 'Estado', sortable: true },
        { key: 'Pais', label: 'País', sortable: true },
        { key: 'CP', label: 'CP', sortable: true },
        { key: 'Telefono1', label: 'Teléfono 1', sortable: true },
        { key: 'Telefono2', label: 'Teléfono 2', sortable: true },
        { key: 'Email', label: 'Email', sortable: true },
        { key: 'ContactoNombre1', label: 'Contacto Nombre', sortable: true },
        { key: 'ContactoPuesto1', label: 'Contacto Puesto', sortable: true },
        { key: 'ContactoTelefono1', label: 'Contacto Teléfono', sortable: true },
        { key: 'ContactoEmail1', label: 'Contacto Email', sortable: true },
        { key: 'MonedaDefault', label: 'Moneda Default', sortable: true },
        { key: 'MetodoPagoDefault', label: 'Método Pago Default', sortable: true },
        { key: 'FormaPagoDefault', label: 'Forma Pago Default', sortable: true },
        { key: 'UsoCFDIDefault', label: 'Uso CFDI Default', sortable: true },
        { key: 'RegimenFiscal', label: 'Régimen Fiscal', sortable: true },
        { key: 'DiasCredito', label: 'Días Crédito', sortable: true },
        { key: 'LimiteCredito', label: 'Límite Crédito', sortable: true },
        { key: 'SaldoActual', label: 'Saldo Actual', sortable: true },
        { 
          key: 'EsActivo', 
          label: 'Activo', 
          sortable: true,
          format: (value) => value ? 'Sí' : 'No'
        },
        { key: 'Notas', label: 'Notas', sortable: true }
      ];
    }
  },
  created() {
    this.service.initialize();
  },
  mounted() {
    // Emitir evento al padre para notificar que esta página tiene toolbar
    this.$emit('toolbar-mounted');
  },
  methods: {
    async loadSociosNegocio() {
      console.log('Cargando socios de negocio...');
      await this.service.listar();
    },
    addSocioNegocio() {
      this.isEditing = false;
      this.openModal();
    },
    async editSocioNegocio(socioNegocio) {
      this.isEditing = true;
      try {
        // Cargar datos completos del socio de negocio
        const response = await this.service.lectura(socioNegocio.Clave);
        if (response && response.data) {
          this.formData = { ...response.data };
        } else {
          this.formData = { ...socioNegocio };
        }
        this.openModal();
      } catch (error) {
        console.error('Error al cargar socio de negocio:', error);
        this.$alert.error('No se pudo cargar el socio de negocio para edición.');
      }
    },
    confirmDelete(clave) {
      console.log('Iniciando confirmación de eliminación para:', clave);
      
      // Usar SweetAlert para la confirmación
      this.$alert.confirm(
        `¿Está seguro que desea eliminar el socio de negocio con clave "${clave}"?`,
        'Confirmar eliminación',
        { icon: 'warning' }
      ).then((result) => {
        console.log('Resultado de confirmación:', result);
        
        if (result.isConfirmed) {
          this.deleteSocioNegocio(clave);
        }
      });
    },
    async deleteSocioNegocio(clave) {
      console.log('Ejecutando eliminación para:', clave);
      
      try {
        const response = await this.service.borrar({ Clave: clave });
        console.log('Respuesta de eliminación recibida:', response);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Socio de negocio eliminado exitosamente');
          // Recargar datos
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo eliminar el socio de negocio. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al eliminar socio de negocio:', error);
        this.$alert.error('No se pudo eliminar el socio de negocio. Por favor, intente nuevamente.');
      }
    },
    async downloadBulk() {
      try {
        await this.service.downloadBulk();
        this.$alert.success('Descarga iniciada correctamente');
      } catch (error) {
        this.$alert.error('Error al descargar: ' + error.message);
      }
    },
    // Métodos del modal
    openModal() {
      if (!this.isEditing) {
        this.resetForm();
      }
      this.showModal = true;
      document.body.classList.add('modal-open');
    },
    closeModal() {
      this.showModal = false;
      this.modalLoading = false;
      this.isEditing = false;
      this.resetForm();
      document.body.classList.remove('modal-open');
    },
    resetForm() {
      this.formData = {
        Clave: '',
        EmpresaId: this.service.selectedEmpresa ? this.service.selectedEmpresa.EmpresaId : 1,
        ClaveErp: '',
        TipoSocio: '',
        NombreComercial: '',
        RazonSocial: '',
        RFC: '',
        CURP: '',
        Calle: '',
        NumExt: '',
        NumInt: '',
        Colonia: '',
        Localidad: '',
        Municipio: '',
        Estado: '',
        Pais: '',
        CP: '',
        Telefono1: '',
        Telefono2: '',
        Email: '',
        ContactoNombre1: '',
        ContactoPuesto1: '',
        ContactoTelefono1: '',
        ContactoEmail1: '',
        MonedaDefault: '',
        MetodoPagoDefault: '',
        FormaPagoDefault: '',
        UsoCFDIDefault: '',
        RegimenFiscal: '',
        DiasCredito: 0,
        LimiteCredito: 0,
        SaldoActual: 0,
        EsActivo: true,
        Notas: ''
      };
    },
    async handleSubmit(formData) {
      this.modalLoading = true;
      try {
        // Convertir campos numéricos
        const dataToSubmit = { ...formData };
        dataToSubmit.EmpresaId = parseInt(dataToSubmit.EmpresaId) || 1;
        dataToSubmit.DiasCredito = parseFloat(dataToSubmit.DiasCredito) || 0;
        dataToSubmit.LimiteCredito = parseFloat(dataToSubmit.LimiteCredito) || 0;
        dataToSubmit.SaldoActual = parseFloat(dataToSubmit.SaldoActual) || 0;
        
        const response = await this.service.guardar(dataToSubmit);
        
        // Verificar si la respuesta es exitosa
        if (response && response.success) {
          // Mostrar mensaje de éxito
          this.$alert.success(response.message || 'Socio de negocio guardado exitosamente');
          // Cerrar modal y recargar datos
          this.closeModal();
          this.service.recargar();
        } else {
          // Si no es exitosa, mostrar error
          this.$alert.error(response.message || 'No se pudo guardar el socio de negocio. Por favor, intente nuevamente.');
        }
      } catch (error) {
        console.error('Error al guardar socio de negocio:', error);
        this.$alert.error('No se pudo guardar el socio de negocio. Por favor, intente nuevamente.');
      } finally {
        this.modalLoading = false;
      }
    }
  }
}
</script>

<style scoped>
.cat-socionegocio {
  min-height: calc(100vh - 200px);
  padding: 20px;
}

.cat-socionegocio-content {
  padding: 0;
}

.page-content {
  padding: 1rem;
}

.toolbar-container {
  margin-bottom: 1.5rem;
}

/* Modal styles */
.modal.show {
  display: block !important;
}

/* Forzar centrado del modal */
.modal {
  padding: 0 !important;
}

.modal-dialog {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
  max-width: 600px;
  width: 90%;
  z-index: 1055;
  max-height: 90vh;
  overflow-y: auto;
}

/* Centrado vertical y horizontal */
.modal.show .modal-dialog {
  transform: translate(-50%, -50%) !important;
  margin: 0 !important;
}

/* Responsivo para móviles */
@media (max-width: 768px) {
  .modal-dialog {
    width: 95% !important;
    max-width: none !important;
    max-height: 95vh;
  }
}

@media (max-width: 576px) {
  .modal-dialog {
    width: 98% !important;
    max-height: 98vh;
  }
}
</style>
